<template>
    <!--
        inline=false (padrão): conteúdo dentro do el-popover, aberto pelo botão.
        inline=true: conteúdo renderizado direto, sempre visível, sem botão e sem fechar.
    -->
    <component
        :is="inline ? 'div' : 'el-popover'"
        v-bind="wrapperProps"
        v-on="wrapperListeners"
    >
        <div :class="['filter-builder', { 'filter-builder--inline': inline }]">
            <!-- Lista de condições ativas (cada filtro filho se renderiza aqui quando ativo) -->
            <div class="filter-builder__body">
                <p v-if="!activeItems.length" class="filter-builder__empty">
                    {{ emptyText }}
                </p>
                <slot name="list" />
            </div>

            <div class="filter-builder__footer">
                <!-- Adicionar condição -->
                <div class="filter-builder__add">
                    <el-button size="small" @click="menuOpen = !menuOpen">
                        {{ activeItems.length ? 'Adicionar outra condição' : 'Selecionar condição' }}
                        <i :class="menuOpen ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
                    </el-button>

                    <div v-if="menuOpen" class="filter-builder__menu">
                        <el-input
                            v-model="menuSearch"
                            size="mini"
                            prefix-icon="el-icon-search"
                            placeholder="Buscar condições"
                        />
                        <ul class="filter-builder__menu-list">
                            <el-tooltip
                                v-for="item in availableItems"
                                :key="item._uid"
                                :disabled="!item.hasTooltip()"
                                placement="right"
                                effect="dark"
                                :open-delay="200"
                            >
                                <div slot="content" class="filter-builder__tooltip">
                                    <VNodes :nodes="item.tooltipContent()" />
                                </div>
                                <li
                                    :class="['filter-builder__menu-item', { 'is-disabled': item.disabled }]"
                                    @click="addCondition(item)"
                                >
                                    <i :class="item.icon"></i>
                                    <span>{{ item.title }}</span>
                                    <i :class="item.disabled ? 'el-icon-lock' : 'el-icon-arrow-right'"></i>
                                </li>
                            </el-tooltip>
                            <li v-if="!availableItems.length" class="filter-builder__menu-empty">
                                Nenhuma condição disponível
                            </li>
                        </ul>
                    </div>
                </div>

                <div class="filter-builder__actions">
                    <el-button type="text" size="small" @click="clearAll">Limpar tudo</el-button>
                    <el-button v-if="!autoApply" type="primary" size="small" @click="apply">Aplicar filtro</el-button>
                </div>
            </div>
        </div>

        <!-- Gatilho (pode ser substituído pelo slot "reference") -->
        <template v-if="!inline" slot="reference">
            <slot name="reference">
                <el-button 
                    icon="el-icon-s-operation"
                    :style="{ 
                        height: '32px',
                        paddingBlock: '2px'
                    }"
                    type="primary"
                    round
                >
                    <!-- plain -->
                    {{ label }}
                    <el-tag v-if="appliedCount" class="filter-builder__badge" size="mini" type="info">
                        {{ appliedCount }}
                    </el-tag>
                    <i class="el-icon-arrow-down ml-2"></i>
                </el-button>
            </slot>
        </template>
    </component>
</template>

<script>
    import { Popover, Button, Input, Badge, Tag, Tooltip } from 'element-ui';

    // Renderiza vnodes (slot #tooltip do filtro filho) ou texto simples
    const VNodes = {
        functional: true,
        render: (h, ctx) => h('span', ctx.props.nodes),
    };

    export default {
        name: 'FilterBuilder',
        provide() {
            return { filterBuilder: this };
        },
        props: {
            label: { type: String, default: 'Filtros' },
            emptyText: { type: String, default: 'Nenhuma condição aplicada ainda' },
            width: { type: [String, Number], default: 640 },
            // Renderiza o conteúdo direto (sem popover, sem botão de abrir/fechar)
            inline: { type: Boolean, default: false },
            // Emite o v-model a cada alteração, sem o botão "Aplicar filtro"
            autoApply: { type: Boolean, default: false },
        },
        data() {
            return {
                visible: false,
                menuOpen: false,
                menuSearch: '',
                items: [], // filtros filhos registrados
            };
        },
        computed: {
            activeItems() {
                return this.items
                    .filter((i) => i.active)
                    .sort((a, b) => a.order - b.order);
            },
            availableItems() {
                const term = this.menuSearch.trim().toLowerCase();
                return this.items.filter(
                    (i) => !i.active && (!term || i.title.toLowerCase().includes(term))
                );
            },
            // Quantos filtros estão realmente aplicados (valor já emitido via v-model)
            appliedCount() {
                return this.items.filter((i) => i.hasValue).length;
            },
            wrapperProps() {
                if (this.inline) return {};
                return {
                    value: this.visible,
                    placement: 'bottom-start',
                    trigger: 'click',
                    width: this.width,
                    visibleArrow: false,
                    popperClass: 'filter-builder-popper',
                };
            },
            wrapperListeners() {
                if (this.inline) return {};
                return {
                    input: (v) => (this.visible = v),
                    hide: this.closeMenu,
                };
            },
        },
        created() {
            this.orderCounter = 0;
            this.autoApplyScheduled = false;
        },
        methods: {
            /* Registro dos filhos */
            register(item) {
                if (!this.items.includes(item)) this.items.push(item);
            },
            unregister(item) {
                this.items = this.items.filter((i) => i !== item);
            },
            nextOrder() {
                return ++this.orderCounter;
            },

            /* Ações */
            addCondition(item) {
                if (item.disabled) return;
                item.activate();
                this.closeMenu();
            },
            closeMenu() {
                this.menuOpen = false;
                this.menuSearch = '';
            },
            apply() {
                // 1) cada filho emite seu v-model  2) depois avisamos o pai
                this.items.forEach((i) => i.apply());
                this.$emit('apply');
                this.visible = false;
            },
            /** Modo auto-apply: agrupa as mudanças do mesmo tick num único 'apply' */
            notifyAutoApply() {
                if (this.autoApplyScheduled) return;
                this.autoApplyScheduled = true;
                this.$nextTick(() => {
                    this.autoApplyScheduled = false;
                    this.$emit('apply');
                });
            },
            clearAll() {
                this.items.forEach((i) => i.clear());
                this.$emit('clear');
                this.$emit('apply');
                this.visible = false;
            },
        },
        components: {
            [Popover.name]: Popover,
            [Button.name]: Button,
            [Input.name]: Input,
            [Badge.name]: Badge,
            [Tag.name]: Tag,
            [Tooltip.name]: Tooltip,
            VNodes,
        },
    };
</script>

<!-- Global: o popper é anexado ao body, então não dá para usar scoped aqui -->
<style>
    .filter-builder-popper {
        padding: 0 !important;
        border-radius: 10px !important;
    }
</style>

<style scoped>
    .filter-builder--inline {
        border: 1px solid #ebeef5;
        border-radius: 10px;
        background: #fff;
    }
    .filter-builder__body {
        /* flex-column: permite que cada linha (FilterBuilderRow) se posicione via `order`,
           seguindo a ordem de ativação e não a ordem de declaração no slot */
        display: flex;
        flex-direction: column;
        padding: 12px 16px;
        border-bottom: 1px solid #ebeef5;
        min-height: 20px;
    }
    .filter-builder__empty {
        margin: 4px 0;
        font-size: 13px;
        color: #909399;
    }
    .filter-builder__footer {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
    }
    .filter-builder__actions {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    .filter-builder__add {
        position: relative;
    }
    .filter-builder__menu {
        position: absolute;
        top: calc(100% + 6px);
        left: 0;
        z-index: 10;
        width: 240px;
        padding: 8px;
        background: #fff;
        border: 1px solid #ebeef5;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    }
    .filter-builder__menu-list {
        margin: 6px 0 0;
        padding: 0;
        list-style: none;
        max-height: 220px;
        overflow-y: auto;
    }
    .filter-builder__menu-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px;
        border-radius: 6px;
        font-size: 13px;
        color: #303133;
        cursor: pointer;
    }
    .filter-builder__menu-item span {
        flex: 1;
    }
    .filter-builder__menu-item:hover {
        background: #f5f7fa;
    }
    .filter-builder__menu-item .el-icon-arrow-right,
    .filter-builder__menu-item .el-icon-lock {
        color: #c0c4cc;
        font-size: 12px;
    }
    .filter-builder__menu-item.is-disabled {
        color: #c0c4cc;
        cursor: not-allowed;
    }
    .filter-builder__menu-item.is-disabled:hover {
        background: transparent;
    }
    .filter-builder__tooltip {
        max-width: 260px;
        line-height: 1.4;
    }
    .filter-builder__menu-empty {
        padding: 8px;
        font-size: 13px;
        color: #909399;
    }
    .filter-builder__badge {
        margin-left: 6px;
    }
    .filter-builder__badge >>> .el-badge__content {
        position: static;
        transform: none;
        border: none;
    }
</style>


// WEBPACK FOOTER //
// resources/assets/js/components/_micro/FilterBuilder/FilterBuilder.vue